// /watch — auto-playing Coach + Player demo tours. Each "scene" pairs a real
// product screenshot/component with a caption, auto-advancing on a timer.
// Pause/scrub/skip controls give visitors full control. No login required.
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, ArrowRight, Sparkles, Share2, Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";

import { BACKEND_URL as API_BASE } from "@/lib/api";

// Build the narration script for a scene (read aloud by OpenAI TTS).
// Prefer the scene's dedicated `script` field (written for spoken delivery) and
// fall back to title + body so the function is always safe. Language-aware.
function narrationFor(scene) {
  if (scene.script && scene.script.trim().length > 0) return scene.script;
  return `${scene.title}. ${scene.body}`;
}

// Apply language overrides ({lang}_es fields) to a scenes array. When lang === 'en'
// or a given field has no translation, the English original is preserved.
function localizedScenes(scenes, lang) {
  if (lang === "en") return scenes;
  return scenes.map((s) => ({
    ...s,
    eyebrow: s[`eyebrow_${lang}`] || s.eyebrow,
    title: s[`title_${lang}`] || s.title,
    body: s[`body_${lang}`] || s.body,
    script: s[`script_${lang}`] || s.script,
  }));
}

const COACH_SCENES = [
  {
    id: "intro",
    eyebrow: "Coach Demo · 60 seconds",
    title: "One score. Five hybrids. Every pitcher. Every day.",
    body: "This is what your morning looks like with ArmIQ. No more guessing about who can throw today and how hard.",
    script: "Welcome to ArmIQ. One score. Five hybrid tracks. Every pitcher, every day. No more guessing.",
    eyebrow_es: "Demo para Coach · 60 segundos",
    title_es: "Una puntuación. Cinco híbridos. Cada lanzador. Cada día.",
    body_es: "Así se ve tu mañana con ArmIQ. Se acabaron las dudas sobre quién puede lanzar hoy y con qué intensidad.",
    script_es: "Bienvenido a ArmIQ. Una puntuación. Cinco vías híbridas. Cada lanzador, cada día. Se acabaron las dudas.",
    visual: "intro",
    duration: 4500,
  },
  {
    id: "scoreboard",
    eyebrow: "00:05 — Roster",
    title: "The whole staff in one glance",
    body: "Color-coded readiness scores for every pitcher. Critical states float to the top. The engine already knows who needs a conversation today.",
    script: "Your roster, color-coded. Greens are ready. Reds are pulled. The engine already flagged who needs a conversation today.",
    eyebrow_es: "00:05 — Plantel",
    title_es: "Todo el plantel en una sola vista",
    body_es: "Puntuaciones de preparación con código de color para cada lanzador. Los estados críticos suben al inicio. El motor ya sabe con quién tienes que hablar hoy.",
    script_es: "Tu plantel, con código de color. Verdes están listos. Rojos están fuera. El motor ya marcó con quién hablar hoy.",
    visual: "roster",
    duration: 6000,
  },
  {
    id: "hotpitcher",
    eyebrow: "00:11 — Real-time during games",
    title: "Hot-Pitcher Live Tile",
    body: "Apple Watch HR + HRV streams during bullpens and games. When a pitcher's HR sustains 90+ over baseline for 3 minutes, the tile pulses red AND you get a push on your phone.",
    script: "During games, ArmIQ streams heart rate and heart-rate variability live. When a pitcher stays ninety beats above baseline for three minutes, the tile pulses red, and your phone buzzes.",
    eyebrow_es: "00:11 — En vivo durante los juegos",
    title_es: "Tile en vivo del lanzador caliente",
    body_es: "El Apple Watch transmite frecuencia cardíaca y variabilidad en bullpens y juegos. Cuando un lanzador supera noventa pulsaciones sobre su base por tres minutos, el tile pulsa rojo Y recibes una alerta en el teléfono.",
    script_es: "Durante los juegos, ArmIQ transmite frecuencia cardíaca y variabilidad en vivo. Cuando un lanzador supera noventa pulsaciones sobre su base por tres minutos, el tile pulsa rojo, y tu teléfono vibra.",
    visual: "hotpitcher",
    duration: 7000,
  },
  {
    id: "workload",
    eyebrow: "00:18 — Beyond throwing",
    title: "Strength coach work counts now",
    body: "Lifts, conditioning, speed-agility, AT rehab — all logged, all verified, all feeding the readiness math. No more invisible work.",
    script: "Lifts. Conditioning. Speed work. Rehab. Every minute logged, verified, and fed into the readiness math. Invisible work, no longer.",
    eyebrow_es: "00:18 — Más allá del lanzamiento",
    title_es: "El trabajo del entrenador de fuerza ahora cuenta",
    body_es: "Pesas, condicionamiento, agilidad, rehabilitación — todo registrado, todo verificado, todo alimenta el cálculo de preparación. Se acabó el trabajo invisible.",
    script_es: "Pesas. Condicionamiento. Velocidad. Rehabilitación. Cada minuto registrado, verificado, y alimentando el cálculo de preparación. El trabajo invisible, ya no.",
    visual: "workload",
    duration: 6000,
  },
  {
    id: "drilldown",
    eyebrow: "00:24 — Sparkline drill-down",
    title: "Click any sparkline → instant context",
    body: "See exactly which days had S&C work and which athletes contributed. Feed it back into pitch-count decisions.",
    script: "Click any sparkline for instant context. See which days the work landed, and which athletes carried it. Then feed it back into your pitch counts.",
    eyebrow_es: "00:24 — Detalle de gráficas",
    title_es: "Haz clic en cualquier gráfica — contexto al instante",
    body_es: "Ve exactamente qué días tuvieron trabajo de fuerza y qué atletas contribuyeron. Aliméntalo a tus decisiones de conteo de lanzamientos.",
    script_es: "Haz clic en cualquier gráfica para contexto instantáneo. Ve qué días aterrizó el trabajo, y qué atletas lo cargaron. Luego intégralo en tus conteos.",
    visual: "drilldown",
    duration: 5500,
  },
  {
    id: "replay",
    eyebrow: "00:30 — Hot-Pitcher Replay",
    title: "Last night's 7th inning, archived",
    body: "Every time a pitcher entered the critical zone, ArmIQ snapshotted the 10-min HR+HRV trace. Review it the next morning. Annotate it. Refine your bullpen rules.",
    script: "Every critical event is archived. Review last night's seventh inning. Annotate it. Tighten your bullpen rules before tomorrow.",
    eyebrow_es: "00:30 — Replay del lanzador caliente",
    title_es: "El séptimo inning de anoche, archivado",
    body_es: "Cada vez que un lanzador entró en zona crítica, ArmIQ guardó el rastro de diez minutos de pulso y variabilidad. Revísalo a la mañana siguiente. Anótalo. Refina tus reglas de bullpen.",
    script_es: "Cada evento crítico está archivado. Revisa el séptimo inning de anoche. Anótalo. Ajusta tus reglas de bullpen antes de mañana.",
    visual: "replay",
    duration: 6000,
  },
  {
    id: "ghost",
    eyebrow: "00:36 — Ghost-pitcher detection",
    title: "Catch hidden injuries 2-3 days earlier",
    body: "Three-tier severity (1d advisory → 2d warning → 3d critical) pings the staff the moment a player goes dark on check-ins. No more disappearing arms.",
    script: "Three-tier ghost alerts. One day quiet — advisory. Two days — warning. Three days — critical. Catch hidden injuries two to three days earlier.",
    eyebrow_es: "00:36 — Detección de lanzador fantasma",
    title_es: "Detecta lesiones ocultas dos a tres días antes",
    body_es: "Tres niveles de severidad (1d aviso → 2d advertencia → 3d crítico) alertan al staff el momento en que un jugador deja de hacer check-in. Se acabaron los brazos que desaparecen.",
    script_es: "Tres niveles de alerta fantasma. Un día sin actividad, aviso. Dos días, advertencia. Tres días, crítico. Detecta lesiones ocultas dos a tres días antes.",
    visual: "ghost",
    duration: 5500,
  },
  {
    id: "cta",
    eyebrow: "Closing · armiqapp.com",
    title: "Healthy arms. Healthy bodies. Elite performance.",
    body: "Foundational team plan starts at $299/yr for 20 seats. HBCU scholarship comps available. Try the live demo →",
    script: "Healthy arms. Healthy bodies. Elite performance. Team plans start at two-ninety-nine a year. Try the live demo at arm-i-q app dot com.",
    eyebrow_es: "Cierre · armiqapp.com",
    title_es: "Brazos sanos. Cuerpos sanos. Rendimiento de élite.",
    body_es: "Plan de equipo desde $299/año para 20 cupos. Becas HBCU disponibles. Prueba la demo en vivo →",
    script_es: "Brazos sanos. Cuerpos sanos. Rendimiento de élite. Planes de equipo desde doscientos noventa y nueve dólares al año. Prueba la demo en vivo en arm-i-q app punto com.",
    visual: "cta",
    duration: 6000,
    isCta: true,
  },
];

const PLAYER_SCENES = [
  {
    id: "intro",
    eyebrow: "Player Demo · 45 seconds",
    title: "Your training prescription. Yours. Every day.",
    body: "ArmIQ turns 30 seconds of daily logging + your watch data into one number: today's hybrid track.",
    script: "Your training prescription. Yours. Every day. Thirty seconds of logging, plus your watch data, equals one number — today's track.",
    eyebrow_es: "Demo para Jugador · 45 segundos",
    title_es: "Tu prescripción de entrenamiento. Tuya. Cada día.",
    body_es: "ArmIQ convierte 30 segundos de registro diario más los datos de tu reloj en un número: la vía híbrida de hoy.",
    script_es: "Tu prescripción de entrenamiento. Tuya. Cada día. Treinta segundos de registro, más los datos de tu reloj, igual un número — la vía de hoy.",
    visual: "player-intro",
    duration: 4500,
  },
  {
    id: "score",
    eyebrow: "00:04 — Today's Plan",
    title: "Readiness 84 — Hybrid A · GAME DAY",
    body: "Your HRV is above baseline, sleep was good, soreness is 1/5. ArmIQ greenlights a Game-Day Hybrid A: long toss + pull-downs.",
    script: "Today: readiness eighty-four. Hybrid A. Game day. Long toss and pull-downs are cleared.",
    eyebrow_es: "00:04 — Plan de hoy",
    title_es: "Preparación 84 — Híbrido A · DÍA DE JUEGO",
    body_es: "Tu variabilidad cardíaca está sobre tu base, dormiste bien, dolor 1/5. ArmIQ aprueba un Híbrido A de día de juego: lanzamiento largo + pull-downs.",
    script_es: "Hoy: preparación ochenta y cuatro. Híbrido A. Día de juego. Lanzamiento largo y pull-downs aprobados.",
    visual: "player-score",
    duration: 5500,
  },
  {
    id: "slider",
    eyebrow: "00:09 — Player tolerance",
    title: "Tap to flex one band up or down",
    body: "Engine says C but the arm feels live? Bump to B. Tight? Step down to D. Your coach sees the engine's pick + your override side-by-side.",
    script: "Engine picked C, but the arm feels live? Tap up to B. Tight today? Step down to D. Your coach sees both — the engine's pick and your override.",
    eyebrow_es: "00:09 — Tolerancia del jugador",
    title_es: "Toca para flexionar una banda arriba o abajo",
    body_es: "¿El motor dice C pero el brazo se siente vivo? Sube a B. ¿Tenso? Baja a D. Tu entrenador ve la elección del motor y tu ajuste lado a lado.",
    script_es: "El motor eligió C, pero el brazo se siente vivo? Sube a B. ¿Tenso hoy? Baja a D. Tu entrenador ve las dos cosas — la elección del motor, y tu ajuste.",
    visual: "player-slider",
    duration: 6000,
  },
  {
    id: "log",
    eyebrow: "00:15 — Daily log",
    title: "30 seconds. That's it.",
    body: "Arm health, pain, sleep, soreness, RPE, post-throw fatigue. Every field your coach toggled on. No more, no less.",
    script: "Thirty seconds. Arm health. Pain. Sleep. Soreness. Exertion. Fatigue. Only the fields your coach turned on. No more, no less.",
    eyebrow_es: "00:15 — Registro diario",
    title_es: "30 segundos. Eso es todo.",
    body_es: "Salud del brazo, dolor, sueño, molestia, esfuerzo percibido, fatiga post-lanzamiento. Solo los campos que tu entrenador activó. Ni más, ni menos.",
    script_es: "Treinta segundos. Salud del brazo. Dolor. Sueño. Molestia. Esfuerzo. Fatiga. Solo los campos que tu entrenador activó. Ni más, ni menos.",
    visual: "player-log",
    duration: 5500,
  },
  {
    id: "biometrics",
    eyebrow: "00:20 — Watch data",
    title: "Seven signals, one Readiness Score",
    body: "HRV, RHR, sleep, REM, VO₂, steps, in-game HR. Auto-synced from your Apple Watch or Wear OS device. You see exactly how each one feeds the engine.",
    script: "Seven signals from your watch — heart-rate variability, resting heart rate, sleep, REM, oxygen capacity, steps, and in-game heart rate — each one feeds the score.",
    eyebrow_es: "00:20 — Datos del reloj",
    title_es: "Siete señales, una puntuación de preparación",
    body_es: "Variabilidad cardíaca, pulso en reposo, sueño, REM, VO₂, pasos, pulso en juego. Sincronizado automáticamente desde tu Apple Watch o dispositivo Wear OS.",
    script_es: "Siete señales de tu reloj — variabilidad cardíaca, pulso en reposo, sueño, sueño REM, capacidad de oxígeno, pasos, y pulso durante el juego — cada una alimenta la puntuación.",
    visual: "player-biometrics",
    duration: 6500,
  },
  {
    id: "attendance",
    eyebrow: "00:26 — Self-log attendance",
    title: "Lifted at 6 AM? Check in. Walk-in at 3 PM? Check in.",
    body: "Your strength coach or AT verifies it, and the load shows up in tomorrow's score. Your gym work and rehab finally count.",
    script: "Lifted at six A.M.? Check in. Walk-in rehab at three? Check in. Your strength coach verifies it, and the load shows up in tomorrow's score.",
    eyebrow_es: "00:26 — Asistencia auto-registrada",
    title_es: "¿Levantaste a las 6 AM? Marca. ¿Rehab a las 3 PM? Marca.",
    body_es: "Tu entrenador de fuerza o trainer lo verifica, y la carga aparece en la puntuación de mañana. Tu trabajo de gimnasio y rehabilitación por fin cuentan.",
    script_es: "¿Levantaste a las seis de la mañana? Marca asistencia. ¿Rehabilitación a las tres? Marca. Tu entrenador de fuerza lo verifica, y la carga aparece en la puntuación de mañana.",
    visual: "player-attendance",
    duration: 5500,
  },
  {
    id: "protection",
    eyebrow: "00:32 — Arm-first",
    title: "Pain during throwing = automatic shutdown",
    body: "Logged arm health ≤ 2 or pain-during? Engine pins you to Hybrid E. Coach + AT get a critical push. The arm is protected. The career is protected.",
    script: "Arm health two or below? Pain during throwing? The engine pins you to hybrid E. Your coach and trainer are paged immediately. The arm is protected.",
    eyebrow_es: "00:32 — El brazo primero",
    title_es: "Dolor durante el lanzamiento = parada automática",
    body_es: "¿Salud del brazo ≤ 2 o dolor durante? El motor te fija en Híbrido E. Entrenador + AT reciben alerta crítica. El brazo está protegido. La carrera está protegida.",
    script_es: "¿Salud del brazo dos o menos? ¿Dolor durante el lanzamiento? El motor te fija en híbrido E. Tu entrenador y trainer son notificados de inmediato. El brazo está protegido.",
    visual: "player-protection",
    duration: 6500,
  },
  {
    id: "cta",
    eyebrow: "Closing · armiqapp.com",
    title: "One score. Five hybrids. Your daily plan.",
    body: "Solo athlete accounts $39/yr. Team plans $299/yr for 20 seats. Try the live demo on your own pace →",
    script: "One score. Five hybrids. Your daily plan. Solo accounts start at thirty-nine a year. Try the live demo at arm-i-q app dot com.",
    eyebrow_es: "Cierre · armiqapp.com",
    title_es: "Una puntuación. Cinco híbridos. Tu plan diario.",
    body_es: "Cuentas individuales $39/año. Planes de equipo $299/año para 20 cupos. Prueba la demo en vivo a tu ritmo →",
    script_es: "Una puntuación. Cinco híbridos. Tu plan diario. Cuentas individuales desde treinta y nueve dólares al año. Prueba la demo en vivo en arm-i-q app punto com.",
    visual: "cta",
    duration: 6000,
    isCta: true,
  },
];

function VisualFrame({ visual, sceneTitle }) {
  // Stylized scene mock — uses real CSS classes so it FEELS like the actual product.
  // Each visual is a key swap that renders a different layout.
  return (
    <div className="relative w-full aspect-[16/10] rounded-xl overflow-hidden ink-card-elevated flex items-center justify-center transition-all duration-500" data-visual={visual}>
      {renderVisual(visual, sceneTitle)}
    </div>
  );
}

function renderVisual(visual, sceneTitle) {
  switch (visual) {
    case "intro":
    case "player-intro":
      return (
        <div className="text-center p-8">
          <Logo size="lg" variant="lockup" dark />
          <div className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight text-white">{sceneTitle}</div>
          <div className="mt-3 inline-block px-3 py-1 rounded-full text-[10px] tracking-[0.25em] font-bold uppercase" style={{ backgroundColor: "rgba(34,211,238,0.15)", color: "var(--brand-secondary)" }}>
            HEALTHY ARMS · HEALTHY BODIES · ELITE PERFORMANCE
          </div>
        </div>
      );
    case "roster":
      return <RosterMock />;
    case "hotpitcher":
      return <HotPitcherMock />;
    case "workload":
      return <WorkloadMock />;
    case "drilldown":
      return <DrilldownMock />;
    case "replay":
      return <ReplayMock />;
    case "ghost":
      return <GhostMock />;
    case "player-score":
      return <PlayerScoreMock score={84} hybrid="A" band="Peak" />;
    case "player-slider":
      return <SliderMock />;
    case "player-log":
      return <LogMock />;
    case "player-biometrics":
      return <BiometricsMock />;
    case "player-attendance":
      return <AttendanceMock />;
    case "player-protection":
      return <PlayerScoreMock score={32} hybrid="E" band="Risk" critical />;
    case "cta":
      return (
        <div className="text-center p-8">
          <img
            src="/armiq-logo.png"
            alt="ArmIQ"
            className="h-32 md:h-40 mx-auto drop-shadow-2xl"
            style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.5))" }}
            data-testid="cta-logo"
          />
          <div className="mt-4 font-display font-black text-2xl md:text-3xl tracking-tight text-white">armiqapp.com</div>
          <div className="mt-2 text-white/70 text-xs uppercase tracking-[0.25em] font-bold">Try the live demo</div>
        </div>
      );
    default:
      return null;
  }
}

function RosterMock() {
  const players = [
    { name: "Jackson Reeves", score: 84, band: "peak", color: "#10B981" },
    { name: "Carter Donovan", score: 76, band: "good", color: "#22D3EE" },
    { name: "Coleman Standish", score: 38, band: "risk", color: "#F87171", critical: true },
    { name: "Ethan Brennan", score: 72, band: "good", color: "#22D3EE" },
    { name: "Liam Costa", score: 88, band: "peak", color: "#10B981" },
    { name: "Maverick Joh.", score: 62, band: "caution", color: "#F59E0B" },
  ];
  return (
    <div className="p-6 w-full">
      <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40 mb-3">Princeton Hill · Roster</div>
      <div className="grid grid-cols-2 gap-2">
        {players.map((p) => (
          <div key={p.name} className="rounded-lg p-3 flex items-center justify-between" style={{ backgroundColor: p.critical ? "rgba(248,113,113,0.12)" : "rgba(255,255,255,0.04)", border: `1px solid ${p.critical ? p.color : "rgba(255,255,255,0.08)"}` }}>
            <div>
              <div className="font-semibold text-white text-sm">{p.name}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] mt-0.5" style={{ color: p.color }}>{p.band}</div>
            </div>
            <div className="font-display font-black text-2xl" style={{ color: p.color }}>{p.score}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HotPitcherMock() {
  return (
    <div className="p-6 w-full">
      <div className="rounded-xl p-5" style={{ backgroundColor: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.4)" }}>
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 animate-pulse"></span>
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Hot-Pitcher (LIVE)</div>
          <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-rose-300">2 ACTIVE</span>
        </div>
        <div className="mt-4 rounded-lg p-3 flex items-center gap-3" style={{ backgroundColor: "rgba(248,113,113,0.12)" }}>
          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold text-white">JR</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-white">Jackson Reeves</span>
              <span className="text-[9px] tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded text-rose-300 border border-rose-500/40">CRITICAL</span>
            </div>
            <div className="text-xs text-white/70 mt-1 font-mono-num">
              <span className="text-rose-300">♥ <b>162</b> bpm (+103 vs 59)</span> · HRV <b>24</b> (−66%) · 3.5m in zone
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkloadMock() {
  return (
    <div className="p-6 w-full">
      <div className="rounded-xl p-5" style={{ backgroundColor: "var(--ink-2)", border: "1px solid var(--ink-border)" }}>
        <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">S&C / AT Workload (7d)</div>
        <div className="font-display font-black text-4xl text-white mt-1">1,720 <span className="text-sm text-white/40">min · 52 sessions</span></div>
        <div className="mt-4 space-y-2">
          {[
            { label: "Strength", val: 900, color: "#F59E0B" },
            { label: "Conditioning", val: 320, color: "#22D3EE" },
            { label: "Speed/Agility", val: 240, color: "#A78BFA" },
            { label: "AT Rehab", val: 260, color: "#34D399" },
          ].map((r) => (
            <div key={r.label} className="flex items-center justify-between text-sm">
              <span className="text-white/70">{r.label}</span>
              <span className="font-mono-num text-white"><b>{r.val}</b><span className="text-white/40 ml-1 text-xs">min</span></span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DrilldownMock() {
  const bars = [40, 0, 55, 0, 30, 75, 0, 90, 20, 0, 60, 0, 50, 80];
  const max = Math.max(...bars);
  return (
    <div className="p-6 w-full">
      <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Strength · 14-day breakdown</div>
      <div className="text-white font-display font-bold text-lg mb-3">900 verified minutes · 18 sessions</div>
      <div className="flex items-end gap-1 h-32">
        {bars.map((v, i) => (
          <div key={i} className="flex-1 rounded-t" style={{ height: `${(v / max) * 100}%`, backgroundColor: "#F59E0B", opacity: v > 0 ? 1 : 0.1, minHeight: 4 }} />
        ))}
      </div>
      <div className="text-[10px] text-white/40 mt-1 text-center">14 days · click any bar for player breakdown</div>
    </div>
  );
}

function ReplayMock() {
  return (
    <div className="p-6 w-full">
      <div className="rounded-xl p-4" style={{ backgroundColor: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.4)" }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-rose-300">REPLAY · Coleman Standish</div>
            <div className="font-display font-bold text-white mt-1">7th-inning shutdown</div>
          </div>
          <span className="text-[9px] tracking-[0.15em] font-extrabold px-1.5 py-0.5 rounded text-rose-300 border border-rose-500/40">CRITICAL</span>
        </div>
        <svg viewBox="0 0 200 60" className="w-full h-20 mt-3">
          <path d="M0,40 L20,38 L40,30 L60,22 L80,18 L100,12 L120,10 L140,14 L160,22 L180,30 L200,34" stroke="#f43f5e" strokeWidth="2" fill="none" />
          <path d="M0,40 L20,38 L40,30 L60,22 L80,18 L100,12 L120,10 L140,14 L160,22 L180,30 L200,34 L200,60 L0,60 Z" fill="url(#grad)" />
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#f43f5e" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
        <div className="text-xs text-white/60 mt-2 italic">"Coleman crossed 160 bpm 8 times during the 7th. Pulled him after pitch 92."</div>
      </div>
    </div>
  );
}

function GhostMock() {
  return (
    <div className="p-6 w-full space-y-2">
      <div className="rounded-lg p-3 flex items-center gap-3" style={{ backgroundColor: "rgba(248,113,113,0.10)", border: "1px solid rgba(248,113,113,0.4)" }}>
        <span className="text-rose-300 text-2xl">🚨</span>
        <div className="flex-1">
          <div className="font-semibold text-white">Wyatt Cole — 3-DAY ALERT</div>
          <div className="text-xs text-white/60 mt-0.5">No daily check-in for 3 days. Possible hidden injury.</div>
        </div>
      </div>
      <div className="rounded-lg p-3 flex items-center gap-3" style={{ backgroundColor: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.4)" }}>
        <span className="text-amber-300 text-xl">⚠</span>
        <div className="flex-1">
          <div className="font-semibold text-white">Sawyer Quinn — 2-day gap</div>
          <div className="text-xs text-white/60 mt-0.5">Missed two consecutive logs. Please follow up.</div>
        </div>
      </div>
    </div>
  );
}

function PlayerScoreMock({ score, hybrid, band, critical }) {
  const colors = { A: "#D90429", B: "#F59E0B", C: "#3B82F6", D: "#22D3EE", E: "#16A34A" };
  const c = colors[hybrid];
  return (
    <div className="p-6 w-full">
      <div className="rounded-2xl p-6 text-white" style={{ backgroundColor: critical ? "rgba(248,113,113,0.08)" : "var(--ink-2)", border: `1px solid ${critical ? "rgba(248,113,113,0.4)" : "var(--ink-border-strong)"}` }}>
        <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>Your Next Track</div>
        <div className="mt-2 font-display font-black text-5xl tracking-tighter">Hybrid {hybrid}</div>
        <div className="mt-4 flex items-center gap-3 rounded-lg p-3" style={{ backgroundColor: `${c}1f`, border: `1px solid ${c}55` }}>
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-full font-display font-black text-lg" style={{ backgroundColor: c, color: "#fff" }}>{hybrid}</span>
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: c }}>Today's Suggested Hybrid</div>
            <div className="text-sm text-white/80">Readiness <span className="font-mono-num font-bold text-white">{score}</span> · band <b>{band}</b></div>
          </div>
        </div>
        {critical && <div className="mt-3 text-xs text-rose-300">🚨 Pain during throwing logged — auto-shutdown to Hybrid E. Coach + AT notified.</div>}
      </div>
    </div>
  );
}

function SliderMock() {
  return (
    <div className="p-6 w-full">
      <div className="rounded-lg p-3" style={{ backgroundColor: "var(--ink-1)", border: "1px solid var(--ink-border)" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">How does the arm feel?</div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300">Feels great — step up</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {["B","C","D"].map((l, i) => (
            <div key={l} className="rounded-lg p-2.5" style={{ backgroundColor: i === 0 ? "rgba(245,158,11,0.13)" : "transparent", border: `1px solid ${i === 0 ? "#F59E0B" : "rgba(255,255,255,0.1)"}` }}>
              <div className="font-display font-black text-xl" style={{ color: ["#F59E0B","#3B82F6","#22D3EE"][i] }}>{l}</div>
              <div className="text-[10px] text-white/60 mt-1">{["Long Toss","Short LT","ROM + Light"][i]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LogMock() {
  const fields = [
    { label: "Arm Health", val: 5, max: 5 },
    { label: "Soreness", val: 1, max: 5 },
    { label: "Sleep (hrs)", val: 7.8, max: 12 },
    { label: "Mood", val: 4, max: 5 },
  ];
  return (
    <div className="p-6 w-full space-y-3">
      {fields.map((f) => (
        <div key={f.label}>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/70">{f.label}</span>
            <span className="font-mono-num font-bold text-white">{f.val}</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 mt-1.5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(f.val / f.max) * 100}%`, backgroundColor: "var(--brand-secondary)" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function BiometricsMock() {
  return (
    <div className="p-6 w-full grid grid-cols-3 gap-2">
      {[
        { l: "HRV", v: "68 ms", c: "#22D3EE" },
        { l: "RHR", v: "54 bpm", c: "#EF4444" },
        { l: "Sleep", v: "7.6 hrs", c: "#A78BFA" },
        { l: "VO₂", v: "52", c: "#F59E0B" },
        { l: "Steps", v: "11,400", c: "#34D399" },
        { l: "HR Max", v: "183", c: "#F472B6" },
      ].map((r) => (
        <div key={r.l} className="rounded p-2 border border-white/10 bg-white/5">
          <div className="text-[9px] tracking-[0.2em] uppercase text-white/40">{r.l}</div>
          <div className="font-display font-black text-base mt-0.5" style={{ color: r.c }}>{r.v}</div>
        </div>
      ))}
    </div>
  );
}

function AttendanceMock() {
  return (
    <div className="p-6 w-full space-y-2">
      {[
        { type: "Strength", min: 45, status: "verified", c: "#F59E0B" },
        { type: "AT Rehab", min: 25, status: "pending", c: "#34D399" },
        { type: "Conditioning", min: 30, status: "verified", c: "#22D3EE" },
      ].map((s, i) => (
        <div key={i} className="rounded border border-white/10 px-3 py-2 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.c }} />
            <span className="text-white">{s.type}</span>
            <span className="text-white/40">· {s.min} min</span>
          </div>
          <span className={s.status === "verified" ? "text-emerald-300 text-xs" : "text-amber-300 text-xs"}>
            {s.status === "verified" ? "✓ verified" : "○ pending"}
          </span>
        </div>
      ))}
    </div>
  );
}

function Tour({ scenes, refTag, lang = "en", tab = "coach" }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [audioMap, setAudioMap] = useState({}); // {sceneId: HTMLAudioElement} — populated incrementally
  const beaconedRef = useRef(new Set()); // dedupe analytics events per (ref, scene)

  // Helper: advance to the next scene; if we're already on the last one, END the tour.
  const advanceOrEnd = () => {
    setIdx((i) => {
      if (i >= scenes.length - 1) {
        setEnded(true);
        setPlaying(false);
        setProgress(1);
        return i; // stay on the last scene
      }
      return i + 1;
    });
  };

  const replay = () => {
    setEnded(false);
    setProgress(0);
    setIdx(0);
    setPlaying(true);
  };

  const scene = scenes[idx];

  // PRELOAD scene narrations: stream pattern — flip audioReady as soon as the
  // FIRST scene is ready and keep loading the rest in the background. This makes
  // the tour start within ~1–2s instead of waiting for all 8 clips upfront.
  useEffect(() => {
    let cancelled = false;
    const built = {};
    setAudioMap({});

    const loadOne = async (s) => {
      try {
        const res = await fetch(`${API_BASE}/api/tts/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: narrationFor(s), voice: "onyx" }),
        });
        if (!res.ok) return null;
        const blob = await res.blob();
        if (cancelled) return null;
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.preload = "auto";
        await new Promise((resolve) => {
          audio.addEventListener("loadedmetadata", () => resolve(), { once: true });
          audio.addEventListener("error", () => resolve(), { once: true });
        });
        if (cancelled) { URL.revokeObjectURL(url); return null; }
        built[s.id] = audio;
        // Publish incrementally so the active scene can start as soon as ITS audio is ready.
        setAudioMap((prev) => ({ ...prev, [s.id]: audio }));
        return audio;
      } catch (_) {
        return null;
      }
    };

    (async () => {
      // Load the first scene first — its appearance in audioMap will unlock the timeline.
      if (scenes.length > 0) {
        await loadOne(scenes[0]);
      }
      if (cancelled) return;
      // Now load the rest in parallel in the background while scene 0 plays.
      await Promise.all(scenes.slice(1).map(loadOne));
    })();

    return () => {
      cancelled = true;
      Object.values(built).forEach((a) => {
        try { a.pause(); } catch (_) { /* noop */ }
        if (a.src && a.src.startsWith("blob:")) URL.revokeObjectURL(a.src);
      });
    };
  }, [scenes]);

  // Fire analytics beacon (optional — only if a ?ref tag was supplied)
  useEffect(() => {
    if (!refTag) return;
    const key = `${refTag}:${lang}:${scene.id}`;
    if (beaconedRef.current.has(key)) return;
    beaconedRef.current.add(key);
    try {
      fetch(`${API_BASE}/api/tts/tour-events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ref: refTag,
          scene_id: scene.id,
          tab,
          lang,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* noop */ }
  }, [idx, refTag, scene.id, tab, lang]);

  // Main timeline driver: progress + auto-advance.
  // Slaves to audio when it's playing; falls back to scene.duration timer otherwise.
  // Depends on the SPECIFIC active scene's audio object (not the whole audioMap)
  // so that other scenes loading in the background do NOT restart the active scene.
  const activeAudio = audioMap[scene.id];
  useEffect(() => {
    setProgress(0);
    // Pause every audio except the active one
    Object.entries(audioMap).forEach(([id, a]) => {
      if (id !== scene.id) {
        try { a.pause(); a.currentTime = 0; } catch (_) { /* noop */ }
      }
    });

    if (!playing) return;
    // If we're expecting narration for THIS scene (not muted) but it hasn't loaded
    // yet, WAIT. This prevents the visual from advancing before the voiceover starts.
    if (!muted && !activeAudio) {
      return;
    }

    if (activeAudio && !muted) {
      // AUDIO-DRIVEN MODE — visual is slaved to audio playback
      activeAudio.currentTime = 0;
      let advanced = false;
      let fallbackRaf = null;
      let safetyTimer = null;

      const onTimeUpdate = () => {
        if (activeAudio.duration && isFinite(activeAudio.duration)) {
          setProgress(Math.min(1, activeAudio.currentTime / activeAudio.duration));
        }
      };
      const advance = () => {
        if (advanced) return;
        advanced = true;
        advanceOrEnd();
      };
      const startRafFallback = () => {
        // Visual-only fallback when audio can't play (autoplay blocked, etc.)
        const start = Date.now();
        const tick = () => {
          if (advanced) return;
          const elapsed = Date.now() - start;
          const pct = Math.min(1, elapsed / scene.duration);
          setProgress(pct);
          if (pct >= 1) advance();
          else fallbackRaf = requestAnimationFrame(tick);
        };
        fallbackRaf = requestAnimationFrame(tick);
      };

      activeAudio.addEventListener("timeupdate", onTimeUpdate);
      activeAudio.addEventListener("ended", advance);

      activeAudio.play().catch(() => startRafFallback());
      // Safety net: if audio hasn't started progressing in 1.5s, fall back.
      safetyTimer = setTimeout(() => {
        if (!advanced && (activeAudio.paused || activeAudio.currentTime === 0)) {
          startRafFallback();
        }
      }, 1500);

      return () => {
        activeAudio.removeEventListener("timeupdate", onTimeUpdate);
        activeAudio.removeEventListener("ended", advance);
        if (fallbackRaf) cancelAnimationFrame(fallbackRaf);
        if (safetyTimer) clearTimeout(safetyTimer);
      };
    }

    // FALLBACK MODE — muted or no audio loaded yet: rAF timer with scene.duration
    const start = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min(1, elapsed / scene.duration);
      setProgress(pct);
      if (pct >= 1) {
        advanceOrEnd();
      } else {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [idx, playing, muted, activeAudio, scene.id, scene.duration, scenes.length]);

  // Pause/resume audio with the play button without re-triggering the timeline driver.
  useEffect(() => {
    if (!activeAudio) return;
    if (playing && !muted) {
      activeAudio.play().catch(() => {});
    } else {
      try { activeAudio.pause(); } catch (_) { /* noop */ }
    }
  }, [playing, muted, activeAudio]);

  const next = () => {
    if (idx >= scenes.length - 1) {
      setEnded(true);
      setPlaying(false);
      setProgress(1);
    } else {
      setEnded(false);
      setIdx(idx + 1);
    }
  };
  const prev = () => {
    setEnded(false);
    setIdx((i) => Math.max(i - 1, 0));
  };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: "var(--ink-2)", border: "1px solid var(--ink-border-strong)" }}>
      {/* Progress dots */}
      <div className="flex gap-1 p-3 border-b border-white/5">
        {scenes.map((s, i) => (
          <button
            key={s.id}
            onClick={() => { setPlaying(false); setIdx(i); }}
            className="flex-1 h-1 rounded-full bg-white/10 relative overflow-hidden"
            data-testid={`tour-dot-${s.id}`}
            aria-label={`Jump to ${s.eyebrow}`}
          >
            <span
              className="absolute left-0 top-0 h-full"
              style={{
                width: i < idx ? "100%" : i === idx ? `${progress * 100}%` : "0%",
                backgroundColor: "var(--brand-secondary)",
                transition: i === idx ? "none" : "width 0.2s",
              }}
            />
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-0 relative">
        {/* Visual */}
        <div className="p-6 md:p-10 flex items-center justify-center" style={{ backgroundColor: "var(--ink-1)" }}>
          <VisualFrame visual={scene.visual} sceneTitle={scene.title} />
        </div>
        {/* Caption */}
        <div className="p-8 md:p-12 flex flex-col justify-center">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
            {scene.eyebrow}
          </div>
          <h2 className="mt-2 font-display font-black text-2xl md:text-4xl tracking-tight text-white leading-tight">
            {scene.title}
          </h2>
          <p className="mt-4 text-white/70 text-sm md:text-base leading-relaxed">{scene.body}</p>
          {scene.isCta && (
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing"><Button data-testid="tour-cta-pricing">See pricing <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
              <Link to="/demo/coleman/d0"><Button variant="outline" data-testid="tour-cta-demo">Try live demo</Button></Link>
            </div>
          )}
        </div>
        {/* End card overlay */}
        {ended && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-md text-center px-6"
            style={{ backgroundColor: "rgba(11,13,16,0.86)" }}
            data-testid="tour-end-card"
          >
            <img
              src="/armiq-logo.png"
              alt="ArmIQ — Healthy Arms. Healthy Bodies. Elite Performance."
              className="h-32 md:h-40 mb-4 drop-shadow-2xl"
              style={{ filter: "drop-shadow(0 6px 20px rgba(0,0,0,0.5))" }}
              data-testid="tour-end-logo"
            />
            <div className="text-[10px] tracking-[0.35em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>
              {lang === "es" ? "Fin del tour" : "End of tour"}
            </div>
            <p className="mt-3 text-white/70 text-sm md:text-base max-w-md">
              {lang === "es"
                ? `Has visto el recorrido completo de ${Math.round(scenes.reduce((a, b) => a + b.duration, 0) / 1000)} segundos. Repítelo, cambia de perspectiva o prueba la demo en vivo.`
                : `You\u2019ve seen the full ${Math.round(scenes.reduce((a, b) => a + b.duration, 0) / 1000)}-second walkthrough. Replay it, switch perspectives, or jump into the live demo.`}
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Button onClick={replay} data-testid="tour-replay" variant="outline">
                <Play className="h-4 w-4 mr-1.5" /> {lang === "es" ? "Repetir" : "Replay"}
              </Button>
              <Link to="/demo/coleman/d0"><Button data-testid="tour-end-demo">{lang === "es" ? "Probar demo en vivo" : "Try live demo"} <ArrowRight className="h-4 w-4 ml-1.5" /></Button></Link>
              <Link to="/pricing"><Button variant="outline" data-testid="tour-end-pricing">{lang === "es" ? "Ver precios" : "See pricing"}</Button></Link>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/5">
        <div className="flex items-center gap-1">
          <button onClick={prev} className="p-2 rounded hover:bg-white/10 text-white/70" data-testid="tour-prev"><SkipBack className="h-4 w-4" /></button>
          <button onClick={() => setPlaying((p) => !p)} className="p-2 rounded hover:bg-white/10 text-white/80" data-testid="tour-play">
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button onClick={next} className="p-2 rounded hover:bg-white/10 text-white/70" data-testid="tour-next"><SkipForward className="h-4 w-4" /></button>
          <button
            onClick={() => setMuted((m) => !m)}
            className="p-2 rounded hover:bg-white/10 text-white/70 ml-1"
            data-testid="tour-mute"
            aria-label={muted ? "Unmute narration" : "Mute narration"}
            title={muted ? "Unmute narration" : "Mute narration"}
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 hidden sm:flex items-center gap-1.5">
          {muted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
          {muted
            ? `Narration muted · ${Math.round(scenes.reduce((a, b) => a + b.duration, 0) / 1000)}s`
            : audioMap[scene.id]
              ? "Narrated tour"
              : "Loading narration…"}
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 font-mono-num">
          {String(idx + 1).padStart(2, "0")} / {String(scenes.length).padStart(2, "0")}
        </div>
      </div>
    </div>
  );
}

function ShareTour({ tab, lang }) {
  const [open, setOpen] = useState(false);
  const [tag, setTag] = useState("");
  const [copied, setCopied] = useState(false);

  const slug = tag.trim().toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "");
  const params = new URLSearchParams();
  if (slug) params.set("ref", slug);
  if (tab !== "coach") params.set("t", tab);
  if (lang && lang !== "en") params.set("lang", lang);
  const qs = params.toString();
  const shareUrl = `${window.location.origin}/watch${qs ? `?${qs}` : ""}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard blocked */ }
  };

  return (
    <div className="mt-6 max-w-md mx-auto" data-testid="share-tour-block">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="mx-auto flex items-center gap-2 text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2 transition"
          data-testid="share-tour-open"
        >
          <Share2 className="h-3.5 w-3.5" />
          Share this tour with a prospect
        </button>
      ) : (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40 mb-2">Send this tour · tracked link</div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Tag (e.g. coach-suno, hbcu-conf)"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="bg-white/5 border-white/10 text-white text-sm h-9"
              data-testid="share-tour-tag-input"
            />
            <Button size="sm" onClick={copy} data-testid="share-tour-copy" className="h-9">
              {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
          <div className="mt-2 text-[10px] text-white/40 font-mono-num break-all">{shareUrl}</div>
          <div className="mt-2 text-[10px] text-white/50">
            We log scene views + completion per tag so you can see which prospects engaged.
          </div>
        </div>
      )}
    </div>
  );
}

export default function WatchDemo() {
  const [searchParams] = useSearchParams();
  const refTag = searchParams.get("ref") || null;
  const initialTab = searchParams.get("t") === "player" ? "player" : "coach";
  const initialLang = searchParams.get("lang") === "es" ? "es" : "en";
  const [tab, setTab] = useState(initialTab);
  const [lang, setLang] = useState(initialLang);
  const baseScenes = tab === "coach" ? COACH_SCENES : PLAYER_SCENES;
  const scenes = localizedScenes(baseScenes, lang);

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--ink-1)" }} data-testid="watch-demo-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/" dark />
          <Link to="/login" className="text-xs text-white/60 hover:text-white">Sign in →</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 pt-10">
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter text-white text-center">
          {lang === "es" ? "ArmIQ en acción" : "Watch ArmIQ in motion"}
        </h1>
        <p className="text-center text-white/65 mt-3 max-w-2xl mx-auto">
          {lang === "es"
            ? "Dos tours cortos del producto real. Elige una perspectiva — el staff o el atleta. Cada escena avanza sola; pausa o salta cuando quieras."
            : "Two short tours showing the actual product. Pick a perspective — the staff side or the athlete side. Each scene auto-advances; pause or scrub at any time."}
        </p>

        <div className="mt-8 inline-flex items-center gap-1 rounded-full p-1 mx-auto border border-white/10 bg-white/5" style={{ display: "flex", width: "fit-content", margin: "32px auto 0" }}>
          <button
            onClick={() => setTab("coach")}
            data-testid="tab-coach"
            className="rounded-full px-5 py-2 text-sm font-bold transition"
            style={{
              backgroundColor: tab === "coach" ? "var(--brand-primary)" : "transparent",
              color: tab === "coach" ? "white" : "rgba(255,255,255,0.6)",
            }}
          >
            {lang === "es" ? "Demo coach · 60s" : "Coach demo · 60s"}
          </button>
          <button
            onClick={() => setTab("player")}
            data-testid="tab-player"
            className="rounded-full px-5 py-2 text-sm font-bold transition"
            style={{
              backgroundColor: tab === "player" ? "var(--brand-primary)" : "transparent",
              color: tab === "player" ? "white" : "rgba(255,255,255,0.6)",
            }}
          >
            {lang === "es" ? "Demo jugador · 45s" : "Player demo · 45s"}
          </button>
        </div>

        {/* Language picker */}
        <div className="mt-4 flex justify-center" data-testid="lang-picker">
          <div className="inline-flex items-center gap-0 rounded-full p-0.5 border border-white/10 bg-white/[0.03]">
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className="rounded-full px-3 py-1 text-[10px] tracking-[0.2em] font-bold uppercase transition"
              style={{
                backgroundColor: lang === "en" ? "rgba(255,255,255,0.12)" : "transparent",
                color: lang === "en" ? "white" : "rgba(255,255,255,0.5)",
              }}
            >
              EN
            </button>
            <button
              onClick={() => setLang("es")}
              data-testid="lang-es"
              className="rounded-full px-3 py-1 text-[10px] tracking-[0.2em] font-bold uppercase transition"
              style={{
                backgroundColor: lang === "es" ? "rgba(255,255,255,0.12)" : "transparent",
                color: lang === "es" ? "white" : "rgba(255,255,255,0.5)",
              }}
            >
              ES
            </button>
          </div>
        </div>

        <div className="mt-8">
          <Tour key={`${tab}-${lang}`} scenes={scenes} refTag={refTag} lang={lang} tab={tab} />
        </div>

        <ShareTour tab={tab} lang={lang} />

        <div className="mt-10 text-center">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Or explore yourself</div>
          <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
            <Link to="/demo/coleman/d0"><Button variant="outline" size="sm">Coleman · Game day</Button></Link>
            <Link to="/demo/wyatt/g3"><Button variant="outline" size="sm">Wyatt · Heavy reliever</Button></Link>
            <Link to="/demo/jasper/w3"><Button variant="outline" size="sm">Jasper · Rehab ramp</Button></Link>
            <Link to="/demo/liam/d1"><Button variant="outline" size="sm">Liam · Clean week</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
